/**
 * MultiSelectWithCheckBox Component
 *
 * A customizable multi-select component that integrates with React Hook Form, featuring checkboxes for selecting multiple options.
 * The component supports debounced input, a "Select All" feature, custom rendering of options and tags, and integrates seamlessly with Material UI's Autocomplete and Checkbox components.
 *
 * This component is designed for use in forms and can be fully customized in terms of appearance, behavior, and data handling.
 *
 * ## Features:
 * - **Multi-Selection**: Allows selecting multiple options with checkboxes.
 * - **Customizable Render Tags**: Supports rendering tags as chips or comma-separated values.
 * - **Debounced Input**: Efficient filtering of options with debounced input.
 * - **"Select All" Option**: Includes a "Select All" feature for selecting/deselecting all options.
 * - **Error Handling**: Automatically displays error messages if form validation fails.
 * - **Customization**: Supports customization of input fields, dropdown paper, and individual option rendering.
 * Readonly behavior:
 * - If `readonly` is true, the user can open the dropdown and view options
* - BUT cannot add/remove/clear selections (no changes allowed)

 * ## Props:
 * - `name` (string): The name of the form field. This is required for form integration with `react-hook-form`.
 * - `label` (string): The label to be displayed for the multi-select field (optional).
 * - `disabled` (boolean): Disables the component when set to `true` (default: `false`).
 * - `loading` (boolean): Indicates whether the data is being loaded. This prevents the display of options while loading (default: `false`).
 * - `options` (T[]): The list of options to be displayed in the dropdown (required).
 * - `required` (boolean): If `true`, marks the field as required in the form (default: `false`).
 * - `getOptionLabel` (function): A function to extract the label to be displayed for each option.
 * - `getOptionSubLabel` (function): A function to extract an optional sub-label to display for each option (optional).
 * - `getOptionValue` (function): A function to extract the value from each option, used to compare with the selected value.
 * - `renderOption` (function): A custom render function for displaying each option (optional).
 * - `renderTags` (function): A custom render function for displaying selected tags (optional).
 * - `renderTagsType` ('chips' | 'comma-separator' | 'default'): Defines how the selected options are rendered (default: 'comma-separator').
 * - `clearIcon` (ReactNode): A custom icon to clear selected values (default: `CancelIcon`).
 * - `placeholder` (string): The placeholder text when no option is selected (default: 'Select...').
 * - `customPaperStyle` (object): Custom styles for the dropdown's paper component (optional).
 * - `customInputProps` (object): Custom input props (optional).
 * - `fullWidth` (boolean): Determines if the component should take up the full width (default: `true`).
 * - `onInputChange` (function): A debounced function to handle input changes and filter the options (optional).
 * - `showSelectAll` (boolean): If `true`, shows a "Select All" option at the top of the dropdown (default: `true`).
 * - `allowMultiInput` (boolean): If `true`, allows selecting multiple options by using brackets and commas in the input field (default: `false`).
 *
 * ## Example Usage:
 * ```tsx
 * <MultiSelectWithCheckBox
 *   name="categories"
 *   label="Select Categories"
 *   options={categoryOptions}
 *   getOptionLabel={(option) => option.name}
 *   getOptionValue={(option) => option.id}
 *   onInputChange={(inputValue) => console.log(inputValue)}
 *   showSelectAll={true}
 * />
 * ```
 * @param {string} name - The field name (required).
 * @param {string} [label] - The label for the multi-select field (optional).
 * @param {boolean} [disabled=false] - Disables the component when `true`.
 * @param {boolean} [loading=false] - Whether the options are loading.
 * @param {T[]} options - List of options to display.
 * @param {boolean} [required=false] - Marks the field as required in the form.
 * @param {(option: T) => string} getOptionLabel - Function to extract the label from each option.
 * @param {(option: T) => string} [getOptionSubLabel] - Function to extract a sub-label for each option (optional).
 * @param {(option: T) => any} getOptionValue - Function to extract the value of each option.
 * @param {(props: any, option: T, state: any) => React.ReactNode} [renderOption] - Custom render function for each option.
 * @param {(value: T[]) => React.ReactNode} [renderTags] - Custom render function for selected tags.
 * @param {('chips' | 'comma-separator' | 'default')} [renderTagsType='comma-separator'] - Type of rendering for selected tags.
 * @param {ReactNode} [clearIcon] - Custom clear icon.
 * @param {string} [placeholder='Select...'] - Placeholder text when no options are selected.
 * @param {object} [customPaperStyle] - Custom styles for the dropdown paper.
 * @param {object} [customInputProps] - Custom props for the input field.
 * @param {boolean} [fullWidth=true] - Whether the component takes up the full width.
 * @param {(inputValue: string) => void} [onInputChange] - Debounced input change handler.
 * @param {boolean} [showSelectAll=true] - Whether to show the "Select All" option.
 * @param {boolean} [allowMultiInput=false] - Whether to allow multiple selections using brackets and commas in the input field.
 */

/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  InputLabel,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CancelIcon from '@mui/icons-material/Cancel'
import { useTheme } from '@mui/material/styles'
import { Controller, useFormContext } from 'react-hook-form'
import { grey } from '../../../colors'
import { ReactComponent as SelectedCheckBox } from '../../../assets/check-box-selected.svg'
import { debounce } from 'lodash'
import { type SxProps, type Theme } from '@mui/system'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { notification } from '../../Notifications'

const icon = <Box border={`1px solid ${grey[350]}`} borderRadius="4px" width="18px" height="18px" />

export type IRenderTagsType = 'chips' | 'comma-separator' | string
export enum RenderTagsType {
  Chips = 'chips',
  CommaSeparator = 'comma-separator',
  Default = 'default'
}

export interface MultiSelectWithCheckBoxProps<T> {
  name: string
  label?: string
  description?: string
  disabled?: boolean
  readonly?: boolean
  loading?: boolean
  options: T[]
  required?: boolean
  getOptionLabel: (option: T) => string
  getOptionSubLabel?: (option: T) => string
  getOptionValue: (option: T) => any
  getOptionDisabled?: (option: T) => any
  renderOption?: (props: any, option: T, state: any) => React.ReactNode
  renderTags?: (value: T[]) => React.ReactNode
  renderTagsType?: IRenderTagsType
  clearIcon?: React.ReactNode
  placeholder?: string
  customPaperStyle?: object
  customInputProps?: object
  fullWidth?: boolean
  onInputChange?: (arg: string) => void
  showSelectAll?: boolean
  textFieldStyles?: React.CSSProperties
  onOptionSelected?: () => void
  onSelectionChange?: (selected: T[]) => void
  sx?: SxProps<Theme>
  descriptionSx?: SxProps<Theme>
  allowMultiInput?: boolean
}

const MultiSelectWithCheckBox = <T,>({
  name,
  label,
  description,
  disabled = false,
  readonly = false,
  loading = false,
  options = [],
  required = false,
  getOptionLabel,
  getOptionSubLabel,
  getOptionValue,
  getOptionDisabled,
  renderOption,
  renderTags,
  renderTagsType = RenderTagsType.CommaSeparator,
  clearIcon = <CancelIcon sx={{ color: grey[950] }} />,
  placeholder = 'Type to search…',
  customPaperStyle = {},
  customInputProps = {},
  fullWidth = true,
  onInputChange = () => {},
  showSelectAll = false,
  textFieldStyles = {},
  sx,
  onOptionSelected,
  onSelectionChange,
  allowMultiInput = false,
  descriptionSx
}: MultiSelectWithCheckBoxProps<T>) => {
  const [input, setInput] = useState<string>('')

  const theme = useTheme()
  const { control } = useFormContext()

  const defaultRenderOption = (props: any, option: T, { selected }: { selected: boolean }) => {
    const label = getOptionLabel(option)
    const subLabel = getOptionSubLabel?.(option)

    return (
      <li {...props} style={{ width: '100%' }}>
        <Box display="flex">
          <Checkbox
            checkedIcon={<SelectedCheckBox width="18px" height="18px" />}
            icon={icon}
            checked={selected}
            // visually show it's not interactive when readonly
            disabled={readonly}
            sx={{ mt: theme.spacing(-2) }}
          />
          <Box display="flex" flexDirection="column">
            <Typography fontSize="14px" color={grey[250]} fontWeight={500}>
              {label}
            </Typography>
            {subLabel && (
              <Typography fontSize="14px" color={grey[850]}>
                {subLabel}
              </Typography>
            )}
          </Box>
        </Box>
      </li>
    )
  }

  const renderTagContent = (value: T[], onFieldChange?: (value: T[]) => void) => {
    switch (renderTagsType) {
      case RenderTagsType.Chips:
        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'flex-start',
              maxWidth: '100%',
              whiteSpace: 'normal'
            }}
          >
            {value.map((option, index) => (
              <Chip
                color="primary"
                key={index}
                label={getOptionLabel(option)}
                deleteIcon={<CancelIcon sx={{ color: `${grey[400]} !important` }} />}
                onDelete={
                  readonly
                    ? undefined
                    : () => {
                        const newValue = value.filter((_, i) => i !== index)
                        if (typeof onFieldChange === 'function') {
                          onFieldChange(newValue)
                        }
                      }
                }
              />
            ))}
          </Box>
        )

      case RenderTagsType.CommaSeparator:
        return (
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginLeft: theme.spacing(1)
            }}
          >
            {value.map((option) => getOptionLabel(option)).join(', ')}
          </Typography>
        )

      default:
        return null
    }
  }

  const getDebouncedInput = useMemo(() => debounce(onInputChange, 500), [onInputChange])

  // Handling the input change with debouncing and value update
  const handleInputChange = (event: React.ChangeEvent<any>, value: string, reason: string) => {
    if (readonly) return
    if (reason === 'input') {
      setInput(value)
      getDebouncedInput(value)
    }
  }

  const SELECT_ALL_OPTION = { __selectAll: true } as const

  const getSortedOptions = React.useCallback(
    (selected: T[] = []) => {
      const selectedSet = new Set(selected?.map(getOptionValue))
      return options.slice().sort((a, b) => {
        const aSelected = selectedSet.has(getOptionValue(a))
        const bSelected = selectedSet.has(getOptionValue(b))
        if (aSelected === bSelected) return 0
        return aSelected ? -1 : 1
      })
    },
    [options, getOptionValue]
  )

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange: onFieldChange, value }, fieldState: { error } }) => {
        const errorMessage = error?.message ?? false

        const handleSelectAll = (): void => {
          if (readonly) return
          if (value?.length === options.length) {
            onFieldChange([])
          } else {
            onFieldChange(options)
          }
          onSelectionChange?.(value?.length === options.length ? [] : options)
        }

        const handleOptionSelected = () => {
          setInput('')
          onOptionSelected?.()
        }

        const sortedOptions = useMemo(() => getSortedOptions(value || []), [getSortedOptions, value])
        const optionsWithSelectAll = useMemo(
          () => (showSelectAll ? [SELECT_ALL_OPTION, ...sortedOptions] : sortedOptions),
          [showSelectAll, sortedOptions]
        )

        // Filter options based on input, working around 'select all' feature
        const getFilteredOptions = (opts: any, state: any) => {
          const search = state.inputValue.trim().toLowerCase()

          if (!search) return opts

          return opts.filter((opt: any) => {
            if (showSelectAll && opt.__selectAll) return true
            const label = getOptionLabel(opt).toLowerCase() ?? ''
            const subLabel = getOptionSubLabel?.(opt)?.toLowerCase() ?? ''
            return label.includes(search) || subLabel.includes(search)
          })
        }

        // Determines if multi input should be executed
        const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
          if (e.key !== 'Enter' || readonly) return

          // Allow multiple inputs if allowed
          if (allowMultiInput) {
            const searched = input.trim()

            // Check formatting of input
            if (searched?.startsWith('[') && searched?.endsWith(']')) {
              e.preventDefault()
              // Extract values from input
              const requestedValues = searched
                .slice(1, -1)
                .split(',')
                .map((v: any) => v.trim().toLowerCase())
                .filter(Boolean)

              // Select options that match the requested values
              const selectedOptions = options.filter((option) =>
                requestedValues.includes(getOptionLabel(option).trim().toLowerCase())
              )

              // Merge newly selected and previously selected with current
              const currentValues = Array.isArray(value) ? value : []
              const alreadySelectedValues: T[] = []
              const mergedOptions = [
                ...currentValues,
                ...selectedOptions.filter((opt) => {
                  const isSelected = currentValues.some((val) => getOptionValue(val) === getOptionValue(opt))
                  if (isSelected) alreadySelectedValues.push(opt)
                  return !currentValues.some((val) => getOptionValue(val) === getOptionValue(opt))
                })
              ]

              // Find and warn about any values not found
              if (requestedValues.length !== selectedOptions.length) {
                const selectedLabels = selectedOptions.map(getOptionLabel)
                const notFound = requestedValues.filter((val: any) =>
                  !selectedLabels.some((label) => label.trim().toLowerCase() === val.trim().toLowerCase())
                )
                if (notFound.length > 0) notification(`The following values were not found and skipped: ${notFound.join(', ')}`, { variant: 'warning' })
              }

              // Find and warn about any values already selected
              if (alreadySelectedValues.length > 0) {
                notification(`The following values were already selected and skipped: ${alreadySelectedValues.map(getOptionLabel).join(', ')}`, { variant: 'warning' })
              }

              onFieldChange(mergedOptions)
              setInput('')
              onSelectionChange?.(mergedOptions)
              onOptionSelected?.()
            }
          }
        }

        return (
          <Box width="100%" minHeight={'72px'}>
            {label && (
              <InputLabel sx={{ marginBottom: 2 }} required={required}>
                {label}
              </InputLabel>
            )}

            <Autocomplete
              filterOptions={getFilteredOptions}
              loading={loading}
              multiple={true}
              size="small"
              value={value || []}
              onChange={(event, newValue) => {
                if (readonly) return
                onFieldChange(newValue)
                handleOptionSelected()
                onSelectionChange?.(newValue)
              }}
              onInputChange={handleInputChange}
              inputValue={input}
              options={loading ? [] : (optionsWithSelectAll as any[])}
              disableCloseOnSelect
              fullWidth={fullWidth}
              // readonly should not allow clearing
              disableClearable={readonly}
              clearIcon={readonly ? null : clearIcon}
              getOptionLabel={getOptionLabel as any}
              isOptionEqualToValue={(option, val) => getOptionValue(option) === getOptionValue(val)}
              getOptionDisabled={getOptionDisabled}
              popupIcon={<ExpandMoreIcon sx={{ color: grey[150] }} />}
              PaperComponent={({ children }) => (
                <Paper elevation={4} sx={{ marginTop: theme.spacing(2), ...customPaperStyle }}>
                  {children}
                </Paper>
              )}
              renderOption={(props: any, option: T, state: any): any => {
                // Select all option
                if (showSelectAll && (option as any).__selectAll) {
                  return (
                    <Box
                      component="li"
                        {...props as React.HTMLAttributes<HTMLLIElement>}
                        sx={{ width: '100%', fontWeight: 500, opacity: readonly ? 0.6 : 1 }}
                        onClick={(e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                          if (readonly) {
                            e.preventDefault()
                            e.stopPropagation()
                            return
                          }
                          handleSelectAll()
                        }}
                      >
                      <Typography fontSize="14px" color={grey[250]} fontWeight={500}>
                        Select All
                      </Typography>
                    </Box>
                  )
                }

                // Block selecting/deselecting options when readonly (but still allow opening/scrolling)
                const { onClick, ...restProps } = props
                const mergedProps = {
                  ...restProps,
                  onClick: (e: any) => {
                    if (readonly) {
                      e.preventDefault()
                      e.stopPropagation()
                      return
                    }
                    onClick?.(e)
                  }
                }

                return renderOption
                  ? renderOption(mergedProps, option, state)
                  : defaultRenderOption(mergedProps, option, state)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(errorMessage)}
                  helperText={errorMessage as string}
                  fullWidth={fullWidth}
                  placeholder={!value || value?.length === 0 ? placeholder : ''}
                  {...customInputProps}
                  inputProps={{
                    ...params.inputProps,
                    // prevents typing/backspace tag removal in readonly mode,
                    // but dropdown still opens
                    readOnly: readonly
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchRoundedIcon style={{ color: grey[400] }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loading && (
                          <CircularProgress
                            size={20}
                            color="inherit"
                            sx={{ position: 'absolute', right: 38 }}
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    style: {
                      flexWrap: 'nowrap',
                      ...textFieldStyles
                    }
                  }}
                  onKeyDown={(e) => { handleKeyDown(e) }}
                />
              )}
              renderTags={renderTags ?? ((val: T[]) => renderTagContent(val, onFieldChange))}
              disabled={disabled}
              noOptionsText={
                (allowMultiInput && input.trim().startsWith('['))
                  ? 'Multi-input: [att1, att2,...]'
                  : 'No Options'
              }
              sx={sx}
            />
            {description && (
              <Typography
                fontSize="14px"
                sx={{ mt: 1, ...descriptionSx }}
              >
                {description}
              </Typography>
            )}
          </Box>
        )
      }}
    />
  )
}

export default MultiSelectWithCheckBox
